import { mapOptionTypes } from 'app/center/asset_panel/asset_options/AssetOptionsPanel';
import { AssetCollection } from 'app/shared/collections/asset_collection.service';
import {
    isValidFieldSchemaType,
    STAT_DEFINITION__PARENT_TYPE_ASSET,
    STAT_DEFINITION__PARENT_TYPE_IP_DEVICE,
    STAT_DEFINITION__PARENT_TYPE_RADIO,
    STAT_DEFINITION__PARENT_TYPE_ZONE,
    StatDefinition,
} from 'app/shared/collections/StatDefinition';
import { StatDisplayFilterSchema, View } from 'app/shared/collections/view';
import { ZoneCollection } from 'app/shared/collections/zones/zone_collection.service';
import { ImsItemTypeDetails, ImsItemTypeRegistry } from 'app/shared/ims_items/ImsItemTypeRegistry';
import {
    createMapOptionValueOption,
    displayStringToOptionValue,
    extractValueFromOptionValue,
    MapOption,
    MapOptionsService,
    MapOptionValue,
    MapOptionValueOption,
} from 'app/shared/map_options/MapOptionsService';
import { SelectionType } from 'app/shared/selection/SelectionType';
import { UnitService } from 'app/shared/unit.service';
import Constants from 'app/shared/utilities/constant';
import { AccessoryManager } from 'app/views/accessories/accessory_manager.service';
import { EmptyIndicatorManager } from 'app/views/accessories/EmptyIndicatorManager';
import { IndicatorManager } from 'app/views/accessories/IndicatorManager';
import { IMSValueGetter } from 'app/views/value_getters/value_getter';
import * as _ from 'lodash';
import { ConfigService } from '../config/config.service';
import { ImageUrlService } from '../shared/image_url.service';
import { Operator } from '../shared/operators';
import { DirectColorRenderer } from '../shared/utilities/direct_color_renderer';
import { ImsObjectVisibilityService } from '../shared/visibility/ImsObjectVisibilityService';
import { FirstAggregator } from './aggregators/first_aggregator';
import { SHOW_LABEL_ALWAYS, SHOW_LABEL_IMMOBILE, SHOW_LABEL_NONE } from './base_view_constants';
import { EntityVisualiser } from './render/entity_visualiser';
import { ImsObjectValueGetter } from './view_types/generic/ImsObjectValueGetter';
import { getStatDisplayFilterFunction } from './view_types/generic/ViewComponent';
import {
    createImsVisualiser,
    createMapOptionForViewIndicatorConfig,
    getIndicatorMapOptionName,
    getVectorHeatMapContext,
} from './view_types/view_utils';
import { IMSVisualiser } from './visualiser';
import { COLOR_LEGEND_ASSET_OPTIONS_TAG } from 'app/center/asset_panel/asset_options/asset_options.component';

export const ASSET_DISPLAY_AGGREGATOR = 'assetDisplayAggregator';
export const ZONE_DISPLAY_AGGREGATOR = 'zoneDisplayAggregator';
export const ASSET_DISPLAY_COLOUR = 'assetDisplayColor';
const ZONE_DISPLAY_COLOUR = 'zoneDisplayColor';
export const ASSET_LABEL_DISPLAY_VISIBILITY = 'assetLabelDisplayVisibility';
export const ZONE_LABEL_DISPLAY_VISIBILITY = 'zoneLabelDisplayVisibility';
export const ASSET_LABEL_DISPLAY_TYPE = 'assetLabelDisplayType';
export const ZONE_LABEL_DISPLAY_TYPE = 'zoneLabelDisplayType';
const WIFI_CONNECTION_DISPLAY_TYPE = 'wifiConnectionDisplayType';
const CELLULAR_CONNECTION_DISPLAY_TYPE = 'cellularConnectionDisplayType';
const OTHER_CONNECTION_DISPLAY_TYPE = 'otherConnectionDisplayType';

export const LABEL_TYPE_OPTION_NAME = 'name';
const LABEL_TYPE_OPTION_COLOUR_BY_VALUE = 'color_by_value';

const ASSET_COLOR_VISUALISER_LOGICAL_NAME = 'color';
const ASSET_TYPE_COLOR_VISUALISER_LOGICAL_NAME = `assetType`;

const ZONE_COLOR_VISUALISER_LOGICAL_NAME = 'color';
const ZONE_TYPE_COLOR_VISUALISER_LOGICAL_NAME = `zoneType`;

export const MAP_OPTION_GROUP_INDICATORS = {
    name: 'Indicators',
    order: 6,
};

const VISIBILITY_GROUP = {
    name: 'Visibility',
    order: 2,
};
const MAP_OPTION_GROUP_DISPLAY = {
    name: 'Display',
    order: 1,
};

// Private class for displaying no assets
export class DisplayNothingEntityVisualiser extends EntityVisualiser {
    constructor() {
        super(undefined, undefined, undefined, undefined, undefined, undefined, undefined, []);
    }

    getAssetBillboardShow(imsAsset, time) {
        return false;
    }

    getAssetLabelShow(imsAsset, time) {
        return false;
    }

    getAssetImage(imsAsset) {
        return Constants.Graphics.NULL_BILLBOARD;
    }

    getAssetColor(imsAsset, time) {
        return undefined;
    }

    getAssetLabelText(imsAsset, time) {
        return '';
    }

    getZoneColor(imsZone, time) {
        return undefined;
    }

    getZoneLabelText(imsZone, time) {
        return '';
    }

    getMasterAssetShow(imsAsset, time) {
        return false;
    }

    getMasterZoneShow(imsZone, time) {
        return false;
    }

    getZoneLabelShow(imsZone, time) {
        return false;
    }

    getZonePolygonShow(imsZone, time) {
        return false;
    }
}

export interface MapBottomChartOption {
    /** The display name for option shown in the drop down */
    name: string;
    /** The data source to apply to the chart */
    dataSource: string;
    /** The field to apply to the chart */
    dataField: string;
    /** The grouping to use for this chart, defaults provided by dashboard item service */
    groupBy?: string;
    /** The aggregation to use, defaults to 'avg' if not specified */
    aggregationType?: string;
}

export interface MapBottomChartDefinition {
    /** Whether this chart only shows when an Asset is selected */
    selectionOnly?: boolean;
    /** A list of options for this chart, shown in a drop down above the chart */
    options: Array<MapBottomChartOption>;
    defaultOptionIndex?: number;
}

/**
 * Given a list of map option objects; pick the first result
 *  which isn't a part of the list to exclude - if possible;
 *  Otherwise default to the first option
 */
function pickDefaultOptionValue(
    options: Array<{ value: string }>,
    logicalNamesToAvoidIfPossible: Array<string>,
) {
    const firstOptionNotInToAvoid = options.find(
        (option) => !logicalNamesToAvoidIfPossible.includes(option.value),
    );
    if (firstOptionNotInToAvoid) {
        // take our first option that's not in the list to avoid if we can find one...
        return firstOptionNotInToAvoid.value;
    }
    if (options.length) {
        // otherwise take the first option
        return options[0].value;
    }
    return null;
}

export class BaseViewController {
    // Various required services
    protected _assetOptionsService: MapOptionsService;
    protected _zoneOptionsService: MapOptionsService;
    protected _imsObjectVisibilityService: ImsObjectVisibilityService;
    protected _imageUrlService: ImageUrlService;
    protected _assetCollection: AssetCollection;
    protected _zoneCollection: ZoneCollection;
    protected _imsItemTypeRegistry: ImsItemTypeRegistry;

    _imsView: View;

    /**
     * @ngdoc property
     * @name accessories
     * @propertyOf ims.map.service:BaseViewController
     * @description
     * A collection of all AccessoryManagers implemented by the view.
     */
    accessories: { [key: string]: AccessoryManager } = {};

    availableAssetMapOptions: { [key: string]: MapOption } = {};
    defaultAssetMapOptions: { [key: string]: any } = {};

    availableZoneMapOptions: { [key: string]: MapOption } = {};
    defaultZoneMapOptions: { [key: string]: any } = {};

    /**
     * @ngdoc property
     * @name mapBottomChartOptions
     * @propertyOf ims.map.service:BaseViewController
     * @description
     * Chart options to be consumed by the bottom panel of the map
     * Example Format:
     * [
     *  {
     *      dataSource: 'ip_device',
     *      dataField: 'latency'
     *  }
     * ]
     */
    mapBottomChartOptions: Array<MapBottomChartDefinition> = [];

    /**
     * @ngdoc property
     * @name centerPanelTags
     * @propertyOf ims.map.service:BaseViewController
     * @description
     * Tag names of the center panels that should be loaded for this view.
     */
    centerPanelTags: string[] = ['centerAggTable', 'centerQuicklist', 'centerMapOptions'];

    /**
     * @ngdoc property
     * @name activeCollections
     * @propertyOf ims.map.service:BaseViewController
     * @description
     * The names of the collections that should be activated for this view.
     * This is used to define which collections will be loading stats data from the API.
     */
    activeCollections: string[];

    /**
     * @ngdoc property
     * @name _assetImsVisualisers
     * @propertyOf ims.map.service:BaseViewController
     * @description
     * (private) The asset IMSVisualisers used by this view.
     * Visualisers should be added via 'addAssetImsVisualiser';
     */
    _assetImsVisualisers: { [key: string]: IMSVisualiser } = {};

    /**
     * @ngdoc property
     * @name _zoneImsVisualisers
     * @propertyOf ims.map.service:BaseViewController
     * @description
     * (private) The zone IMSVisualisers used by this view.
     * Visualisers should be added via 'addZoneImsVisualiser';
     */
    _zoneImsVisualisers: { [key: string]: IMSVisualiser } = {};

    /**
     * @ngdoc property
     * @name _heatMapImsVisualisers
     * @propertyOf ims.map.service:BaseViewController
     * @description
     * (private) The IMSVisualisers used by this view for heat map rendering.
     * Visualisers should be added via 'addHeatMapImsVisualiser';
     */
    _heatMapImsVisualisers = {};

    /**
     * A reference to the currently active asset visualiser
     * @type {IMSVisualiser}
     * @private
     */
    _currentAssetVisualizer: IMSVisualiser = undefined;

    /**
     * A reference to the currently active zone visualiser
     * @type {IMSVisualiser}
     * @private
     */
    _currentZoneVisualizer: IMSVisualiser = undefined;

    /**
     * @ngdoc property
     * @name _heatMapImsVisualisers
     * @propertyOf ims.map.service:BaseViewController
     * @description
     * (private) The IMSVisualisers used by this view for heat map rendering.
     * Visualisers should be added via 'addHeatMapImsVisualiser';
     */
    _heatMapImsFilters: { [key: string]: IMSVisualiser } = {};

    /**
     * @ngdoc property
     * @name _heatMapLinkedAssetImsVisualisers
     * @propertyOf ims.map.service:BaseViewController
     * @description
     * (private) The asset IMSVisualisers that are required by their respective heat map visualiser,
     * keyed by logical name. If an asset IMSVisualiser is added to this, the asset visualiser will be
     * automatically selected when a heat map of the same logical name is generated.
     * Linked asset visualisers should be added via 'addHeatMapImsVisualiser';
     */
    _heatMapLinkedAssetImsVisualisers = {};

    // Used as the standard for type visualisations for Assets/Zones
    protected _assetTypeVisualiser: IMSVisualiser = null;
    protected _zoneTypeVisualiser: IMSVisualiser = null;

    // Used as the standard for base color visualisations for Assets/Zones
    protected _assetBaseColorVisualiser: IMSVisualiser = null;
    protected _zoneBaseColorVisualiser: IMSVisualiser = null;

    // This defines the infobox component that will be used based on selection type
    // the default asset and ip device components have their contents generated
    // automatically based on the view and/or dynamic stat configs.
    // CHANGE THESE MAPPINGS IN SUBCLASS TO CUSTOMISE
    _infoBoxComponents = {
        [SelectionType.IP_DEVICE]: 'generic-info-box-ip-device',
        [SelectionType.ASSET]: 'generic-info-box-asset',
        [SelectionType.ZONE]: 'info-box-zone',
        [SelectionType.KML_FEATURE_LAYER]: 'info-box-feature-layer',
        [SelectionType.RF_TRANSMITTER]: 'info-box-rf-transmitter',
        [SelectionType.HEAT_MAP]: 'info-box-heat-map',
    };

    public indicatorManager: IndicatorManager;

    constructor(
        assetOptionsService: MapOptionsService,
        zoneOptionsService: MapOptionsService,
        imageUrlService: ImageUrlService,
        imsObjectVisibilityService: ImsObjectVisibilityService,
        assetCollection: AssetCollection,
        zoneCollection: ZoneCollection,
        imsItemTypeRegistry: ImsItemTypeRegistry,
    ) {
        this._assetOptionsService = assetOptionsService;
        this._zoneOptionsService = zoneOptionsService;
        this._assetCollection = assetCollection;
        this._zoneCollection = zoneCollection;
        this._imsItemTypeRegistry = imsItemTypeRegistry;

        // Append zone controls to center panel tags if enabled
        if (ConfigService.zones) {
            this.centerPanelTags.push('centerZoneControls');
        }

        this._imageUrlService = imageUrlService;
        this._imsObjectVisibilityService = imsObjectVisibilityService;

        // inheritance is hard, or at least it is for me;
        // if you want change this thing, do it in the super class and leave this little man be.
        this.indicatorManager = new EmptyIndicatorManager();

        this._setUpBaseColorVisualisers();
        this._setUpTypeVisualisers();

        this._assetOptionsService.addOnMapOptionsChanged(() => {
            this._currentAssetVisualizer = this.getAssetVisualiserForCurrentMapOptions();
        });

        this._zoneOptionsService.addOnMapOptionsChanged(() => {
            this._currentZoneVisualizer = this.getZoneVisualiserForCurrentMapOptions();
        });

        this.clearControllerValues();
    }

    /**
     * The idea of this is to create some simple 'type' visualisers that can be used as a stand-in
     * any time that a visualiser is not available, and for the base type selection for when there are
     * no stats configured to be in a view
     * @private
     */
    private _setUpTypeVisualisers() {
        // Zone Type Visualiser (created up here to use as a stand-in for 'no visualiser' situations)
        const zoneTypeColorRenderer = new DirectColorRenderer();
        const zoneTypeColorValueGetter = new ImsObjectValueGetter('typeCesiumColor');
        const zoneTypeNameValueGetter = new ImsObjectValueGetter('typeName');
        const blankAggregator = [new FirstAggregator()];

        const zoneTypeColorEntityVisualiser = new EntityVisualiser(
            zoneTypeColorRenderer,
            zoneTypeColorValueGetter,
            this._imageUrlService,
            this._assetOptionsService,
            this._zoneOptionsService,
            this._imsObjectVisibilityService,
            this._imsItemTypeRegistry,
            blankAggregator,
            zoneTypeNameValueGetter,
        );

        this._zoneTypeVisualiser = new IMSVisualiser(
            ZONE_TYPE_COLOR_VISUALISER_LOGICAL_NAME,
            'zone',
            'Zone Type',
            zoneTypeColorRenderer,
            zoneTypeColorValueGetter,
            zoneTypeColorEntityVisualiser,
            blankAggregator,
            null,
            null,
        );

        // Asset Type Visualiser (created up here to use as a stand-in for 'no visualiser' situations)
        const assetTypeColorRenderer = new DirectColorRenderer();
        const assetTypeColorValueGetter = new ImsObjectValueGetter('typeCesiumColor');
        const assetTypeNameValueGetter = new ImsObjectValueGetter('typeName');

        const assetTypeColorEntityVisualiser = new EntityVisualiser(
            assetTypeColorRenderer,
            assetTypeColorValueGetter,
            this._imageUrlService,
            this._assetOptionsService,
            this._zoneOptionsService,
            this._imsObjectVisibilityService,
            this._imsItemTypeRegistry,
            blankAggregator,
            assetTypeNameValueGetter,
        );

        this._assetTypeVisualiser = new IMSVisualiser(
            ASSET_TYPE_COLOR_VISUALISER_LOGICAL_NAME,
            'asset',
            'Asset Type',
            assetTypeColorRenderer,
            assetTypeColorValueGetter,
            assetTypeColorEntityVisualiser,
            blankAggregator,
            null,
            null,
        );
    }

    /**
     * Setting up base color visualisers for assets and zones as a color by option
     * @private
     */
    private _setUpBaseColorVisualisers() {
        // Zone Visualiser
        const zoneBaseColorRenderer = new DirectColorRenderer();
        const zoneBaseColorValueGetter = new ImsObjectValueGetter('color');
        const zoneBaseNameValueGetter = new ImsObjectValueGetter('labelName');
        const blankAggregator = [new FirstAggregator()];

        const zoneBaseColorEntityVisualiser = new EntityVisualiser(
            zoneBaseColorRenderer,
            zoneBaseColorValueGetter,
            this._imageUrlService,
            this._assetOptionsService,
            this._zoneOptionsService,
            this._imsObjectVisibilityService,
            this._imsItemTypeRegistry,
            blankAggregator,
            zoneBaseNameValueGetter,
        );

        this._zoneBaseColorVisualiser = new IMSVisualiser(
            ZONE_COLOR_VISUALISER_LOGICAL_NAME,
            'zone',
            'Zone',
            zoneBaseColorRenderer,
            zoneBaseColorValueGetter,
            zoneBaseColorEntityVisualiser,
            blankAggregator,
            null,
            null,
        );

        // Asset Visualiser
        const assetBaseColorRenderer = new DirectColorRenderer();
        const assetBaseColorValueGetter = new ImsObjectValueGetter('color');
        const assetBaseNameValueGetter = new ImsObjectValueGetter('labelName');

        const assetBaseColorEntityVisualiser = new EntityVisualiser(
            assetBaseColorRenderer,
            assetBaseColorValueGetter,
            this._imageUrlService,
            this._assetOptionsService,
            this._zoneOptionsService,
            this._imsObjectVisibilityService,
            this._imsItemTypeRegistry,
            blankAggregator,
            assetBaseNameValueGetter,
        );

        this._assetBaseColorVisualiser = new IMSVisualiser(
            ASSET_COLOR_VISUALISER_LOGICAL_NAME,
            'asset',
            'Asset',
            assetBaseColorRenderer,
            assetBaseColorValueGetter,
            assetBaseColorEntityVisualiser,
            blankAggregator,
            null,
            null,
        );
    }

    /**
     * This is just for test assertions. It doesn't hurt though!
     */
    get zoneTypeVisualiser() {
        return this._zoneTypeVisualiser;
    }

    /**
     * @ngdoc method
     * @name addAssetImsVisualiser
     * @methodOf ims.map.service:BaseViewController
     * @param {IMSVisualiser} visualiser The imsVisualiser to add
     * @description
     * Adds an IMSVisualiser for assets. This adds the visualiser to the available visualisers
     * that are used to colour asset billboards on the Cesium map.
     */
    addAssetImsVisualiser(visualiser: IMSVisualiser) {
        // Human name is used to show the stat in anything that is printed text
        const { displayName, logicalName } = visualiser;

        // Add humanName to availableAssetMapOptions
        // Get list of visualiser aggregators and add dependent map options for them
        const aggregators = visualiser.entityVisualiser.aggregators;

        if (!aggregators.length) {
            return;
        }

        const unwantedMapOptionsList = [
            '_ims_network_stat_wifi_radio_technology',
            '_ims_network_stat_cellular_radio_technology',
            '_ims_network_stat_other_radio_technology',
        ];

        if (
            !unwantedMapOptionsList.includes(visualiser.logicalName) &&
            (visualiser.colorRenderer !== undefined || visualiser.logicalName === 'lte_stats_eci')
        ) {
            this.addAssetMapOption(
                ASSET_DISPLAY_COLOUR,
                logicalName,
                displayName,
                visualiser.sourceDynamicStatDisplayName,
            );
        }

        const aggDisplayNames = aggregators.map((agg) => {
            return createMapOptionValueOption(agg.displayName);
        });

        const mapOptionValue: MapOptionValue = {
            options: aggDisplayNames,
            defaultOption: extractValueFromOptionValue(aggDisplayNames[0]),
        };

        //key: string, dependsOnKey: string, dependsOnValue: string, value: MapOptionValue
        MapOptionsService.setMapOptionDependentValue(
            this.availableAssetMapOptions[ASSET_DISPLAY_AGGREGATOR],
            ASSET_DISPLAY_COLOUR,
            logicalName,
            mapOptionValue,
        );

        // TODO make this not ugly, because boy is it ever.
        // pick the default asset coloring map option
        this.availableAssetMapOptions[ASSET_DISPLAY_COLOUR].value.defaultOption =
            pickDefaultOptionValue(
                this.availableAssetMapOptions[ASSET_DISPLAY_COLOUR].value.options,
                [ASSET_COLOR_VISUALISER_LOGICAL_NAME, ASSET_TYPE_COLOR_VISUALISER_LOGICAL_NAME],
            );

        this.defaultAssetMapOptions[ASSET_DISPLAY_COLOUR] =
            this.availableAssetMapOptions[ASSET_DISPLAY_COLOUR].value.defaultOption;

        // Add the visualiser to the list of asset visualisers in this view
        this._assetImsVisualisers[logicalName] = visualiser;
    }

    /**
     * @ngdoc method
     * @name addZoneImsVisualiser
     * @methodOf ims.map.service:BaseViewController
     * @param {IMSVisualiser} visualiser The imsVisualiser to add
     * @param {Boolean} canColorBy Whether or not this visualiser can be used to color things
     * @description
     * Adds an IMSVisualiser for zones. This adds the visualiser to the available visualisers
     * that are used to colour zone polygons on the Cesium map.
     */
    addZoneImsVisualiser(visualiser: IMSVisualiser, canColorBy = true) {
        // Human name is used to show the stat in anything that is printed text
        const { displayName, logicalName } = visualiser;

        // Only add the mapOptionValue for coloring/aggregate options if canColorBy is true
        if (canColorBy) {
            // Add humanName to availableZoneMapOptions
            this.addZoneMapOption(
                ZONE_DISPLAY_COLOUR,
                logicalName,
                displayName,
                visualiser.sourceDynamicStatDisplayName,
            );

            // Get list of visualiser aggregators and add dependent map options for them
            const aggregators = visualiser.entityVisualiser.aggregators;
            let aggDisplayNames: MapOptionValueOption[] = [createMapOptionValueOption('None')];
            if (aggregators && aggregators.length > 0) {
                aggDisplayNames = aggregators.map((agg) => {
                    return createMapOptionValueOption(agg.displayName);
                });
            }

            const mapOptionValue: MapOptionValue = {
                options: aggDisplayNames,
                defaultOption: extractValueFromOptionValue(aggDisplayNames[0]),
            };
            //key: string, dependsOnKey: string, dependsOnValue: string, value: MapOptionValue
            MapOptionsService.setMapOptionDependentValue(
                this.availableZoneMapOptions[ZONE_DISPLAY_AGGREGATOR],
                ZONE_DISPLAY_COLOUR,
                logicalName,
                mapOptionValue,
            );
        }

        // TODO make this not ugly, because boy is it ever.

        // pick the default zone coloring map option
        this.availableZoneMapOptions[ZONE_DISPLAY_COLOUR].value.defaultOption =
            pickDefaultOptionValue(
                this.availableZoneMapOptions[ZONE_DISPLAY_COLOUR].value.options,
                [ZONE_COLOR_VISUALISER_LOGICAL_NAME, ZONE_TYPE_COLOR_VISUALISER_LOGICAL_NAME],
            );

        this.defaultZoneMapOptions[ZONE_DISPLAY_COLOUR] =
            this.availableZoneMapOptions[ZONE_DISPLAY_COLOUR].value.defaultOption;

        this._zoneImsVisualisers[logicalName] = visualiser;
    }

    /**
     * @ngdoc method
     * @name addHeatMapImsVisualiser
     * @methodOf ims.map.service:BaseViewController
     * @param {IMSVisualiser} visualiser The imsVisualiser to add
     * @param {IMSVisualiser} linkedAssetVisualiser The asset visualiser will be changed to this automatically when
     * a heat map using the provided heat map IMSVisualiser is generated.
     * @description
     * Adds an IMSVisualiser for heat maps. This adds the visualiser to the available visualisers
     * that are used to generate heat maps on the Cesium map.
     */
    addHeatMapImsVisualiser(visualiser: IMSVisualiser, linkedAssetVisualiser = null) {
        this._heatMapImsVisualisers[visualiser.logicalName] = visualiser;
        this._heatMapLinkedAssetImsVisualisers[visualiser.logicalName] = linkedAssetVisualiser;
    }

    /**
     * @ngdoc method
     * @name assetImsVisualisers
     * @methodOf ims.map.service:BaseViewController
     * @description
     * Getter for _assetImsVisualisers
     */
    get assetImsVisualisers() {
        return this._assetImsVisualisers;
    }

    /**
     * @ngdoc method
     * @name zoneImsVisualisers
     * @methodOf ims.map.service:BaseViewController
     * @description
     * Getter for _zoneImsVisualisers
     */
    get zoneImsVisualisers() {
        return this._zoneImsVisualisers;
    }

    /**
     * @ngdoc method
     * @name heatMapImsVisualisers
     * @methodOf ims.map.service:BaseViewController
     * @description
     * Getter for _heatMapImsVisualisers
     */
    get heatMapImsVisualisers() {
        return this._heatMapImsVisualisers;
    }

    /**
     * @ngdoc method
     * @name heatMapLinkedAssetImsVisualisers
     * @methodOf ims.map.service:BaseViewController
     * @description
     * Getter for _heatMapLinkedAssetImsVisualisers
     */
    get heatMapLinkedAssetImsVisualisers() {
        return this._heatMapLinkedAssetImsVisualisers;
    }

    /**
     * @ngdoc method
     * @name currentAssetVisualiser
     * @methodOf ims.map.service:BaseViewController
     * @description
     * Returns the current asset visualiser.
     */
    get currentAssetVisualiser() {
        return this._currentAssetVisualizer;
    }

    /**
     * @ngdoc method
     * @name currentZoneVisualiser
     * @methodOf ims.map.service:BaseViewController
     * @description
     * Returns the current zone visualiser.
     */
    get currentZoneVisualiser() {
        return this._currentZoneVisualizer;
    }

    /**
     * @ngdoc method
     * @name addHeatMapImsFilter
     * @methodOf ims.map.service:BaseViewController
     * @param {IMSVisualiser} visualiser The imsVisualiser to add
     * @description
     * Adds an IMSVisualiser for heat map filters.
     * This is used instead of just using all visualisers because not all filters can be
     * visualisers.
     */
    addHeatMapImsFilter(visualiser: IMSVisualiser) {
        const key = `${visualiser.tableName}_${visualiser.logicalName}`;
        this._heatMapImsFilters[key] = visualiser;
    }

    private applyViewComponentsAsMapOptions() {
        for (const indicatorConfig of [
            ...(this._imsView.indicators || []),
            ...(this._imsView.vectors || []),
            ...(this._imsView.labels || []),
            ...(this._imsView.connectionLines || []),
            ...(this._imsView.polygonVisualizers || []),
            ...(this._imsView.lineVisualizers || []),
            ...(this._imsView.locationPropagations || []),
        ]) {
            const name = getIndicatorMapOptionName(indicatorConfig);
            const option = createMapOptionForViewIndicatorConfig(indicatorConfig);
            this.setAssetMapOptions(name, option);
        }
    }

    addImsVisualisers(imsItemTypeDetails: { [type: string]: ImsItemTypeDetails }) {
        Object.entries(imsItemTypeDetails).map(([imsStatTypeName, statTypeDetails]) => {
            const parentType = statTypeDetails.statDefinition.parent_type;
            const isZoneStat = parentType === STAT_DEFINITION__PARENT_TYPE_ZONE;
            const isAssetStat = parentType === STAT_DEFINITION__PARENT_TYPE_ASSET;
            const isIPeviceStat = parentType === STAT_DEFINITION__PARENT_TYPE_IP_DEVICE;
            const isRadioStat = parentType === STAT_DEFINITION__PARENT_TYPE_RADIO;

            // skip creating anything we'll only throw away if we don't need it
            if (!(isZoneStat || isAssetStat || isIPeviceStat || isRadioStat)) {
                return;
            }

            let mergedStatDefinition: StatDefinition = UnitService.overWriteFieldSchemaUnits(
                this._imsView,
                statTypeDetails.statDefinition,
                imsStatTypeName,
            );

            // This one just merges all the other fields, the units will already match whats in
            // the view table.
            if (this._imsView.tables != null) {
                mergedStatDefinition = _.merge(
                    statTypeDetails.statDefinition,
                    this._imsView.tables[imsStatTypeName],
                );
            }

            // not-empty = explicit, empty = nothing, null = gratuitous
            const mapColorFollowRelationshipConfig = this._imsView.mapColorFollowRelationship.find(
                (config) => config.src_schemas?.includes(imsStatTypeName) || !config.src_schemas,
            );

            // add a visualizer used to access stat data
            for (const fieldName in mergedStatDefinition.field_schemas) {
                const fieldSchema = mergedStatDefinition.field_schemas[fieldName];

                if (!isValidFieldSchemaType(fieldSchema.type)) {
                    // front-end doesn't know how to handle this field type
                    //  that's okay just skip it entirely.
                    continue;
                }

                // If visualiser already exists or the field is hidden, skip
                if (this._assetImsVisualisers[fieldName] || fieldSchema.hidden) {
                    continue;
                }

                // This is any filters defined as a stat_display_filter in view components
                const statFilterFn = getStatDisplayFilterFunction(this);

                let visualizer;
                try {
                    visualizer = createImsVisualiser(
                        statTypeDetails,
                        fieldSchema,
                        fieldName,
                        this._assetCollection,
                        this._zoneCollection,
                        this._imageUrlService,
                        this._assetOptionsService,
                        this._zoneOptionsService,
                        () => statFilterFn,
                        this._imsObjectVisibilityService,
                        null,
                        this._imsItemTypeRegistry,
                        mapColorFollowRelationshipConfig,
                    );
                } catch (e) {
                    console.warn(e);
                    continue;
                }

                // For zone parents we only want zone visualisers
                if (isZoneStat) {
                    this.addZoneImsVisualiser(visualizer);
                    continue;
                }

                const vectorHeatMapContext = getVectorHeatMapContext(
                    this._imsView,
                    visualizer,
                    fieldName,
                );

                // create duplicated visualiser w/ vector additions as applicable
                if (vectorHeatMapContext) {
                    let visualiserWithVectorContext;

                    try {
                        visualiserWithVectorContext = createImsVisualiser(
                            statTypeDetails,
                            fieldSchema,
                            fieldName,
                            this._assetCollection,
                            this._zoneCollection,
                            this._imageUrlService,
                            this._assetOptionsService,
                            this._zoneOptionsService,
                            () => statFilterFn,
                            this._imsObjectVisibilityService,
                            vectorHeatMapContext,
                            this._imsItemTypeRegistry,
                            null,
                        );
                    } catch (e) {
                        console.warn(e);
                        continue;
                    }

                    this.addHeatMapImsVisualiser(visualiserWithVectorContext);
                    this.addHeatMapImsFilter(visualiserWithVectorContext);
                }

                // For asset / ip device / radio parents we want both heat map and asset visualisers
                if (
                    visualizer.colorRenderer !== undefined ||
                    visualizer.logicalName === 'lte_stats_eci'
                ) {
                    this.addHeatMapImsVisualiser(visualizer);
                }
                this.addAssetImsVisualiser(visualizer);
                this.addHeatMapImsFilter(visualizer);
            }
        });
    }

    /**
     * @ngdoc method
     * @name heatMapImsVisualisers
     * @methodOf ims.map.service:BaseViewController
     * @description
     * Getter for _heatMapImsVisualisers
     */
    get heatMapImsFilters() {
        return this._heatMapImsFilters;
    }

    getAssetVisualiserForCurrentMapOptions() {
        // This is returned instead of null to make sure there are no downstream issues
        if (!this._assetOptionsService.selectedMapOptions) {
            return this._assetTypeVisualiser;
        }
        const visLogicalName = this._assetOptionsService.selectedMapOptions[ASSET_DISPLAY_COLOUR];
        const visualisers = Object.values(this._assetImsVisualisers);
        return visualisers.find((v) => v.logicalName === visLogicalName);
    }

    getConnectionDisplayTypeVisualiserForCurrentMapOptions() {
        // This is returned instead of null to make sure there are no downstream issues
        if (!this._assetOptionsService.selectedMapOptions) {
            return this._assetTypeVisualiser;
        }
        const wifiConnectionDisplayName =
            this._assetOptionsService.selectedMapOptions[WIFI_CONNECTION_DISPLAY_TYPE];

        const cellularConnectionDisplayName =
            this._assetOptionsService.selectedMapOptions[CELLULAR_CONNECTION_DISPLAY_TYPE];
        const otherConnectionDisplayName =
            this._assetOptionsService.selectedMapOptions[OTHER_CONNECTION_DISPLAY_TYPE];

        const visualisers = Object.values(this._assetImsVisualisers);
        const wifiVisualiser = visualisers.find((v) => v.displayName === wifiConnectionDisplayName);
        const cellularVisualiser = visualisers.find(
            (v) => v.displayName === cellularConnectionDisplayName,
        );
        const otherVisualiser = visualisers.find(
            (v) => v.displayName === otherConnectionDisplayName,
        );

        return {
            wifiVisualiser: wifiVisualiser,
            cellularVisualiser: cellularVisualiser,
            otherVisualiser: otherVisualiser,
        };
    }

    getZoneVisualiserForCurrentMapOptions() {
        // This is returned instead of null to make sure there are no downstream issues
        if (!this._zoneOptionsService.selectedMapOptions) {
            return this._zoneTypeVisualiser;
        }
        const visLogicalName = this._zoneOptionsService.selectedMapOptions[ZONE_DISPLAY_COLOUR];
        const visualisers = Object.values(this._zoneImsVisualisers);
        const result = visualisers.find((v) => v.logicalName === visLogicalName);
        // If we don't get a match on visualisers.find, return a valid default
        return result || this._zoneTypeVisualiser;
    }

    /**
     * @ngdoc method
     * @name infoBoxComponents
     * @methodOf ims.map.service:BaseViewController
     * @description
     * Returns the infobox component mappings for this view.
     */
    get infoBoxComponents() {
        return this._infoBoxComponents;
    }

    /**
     * @ngdoc method
     * @name addAccessory
     * @methodOf ims.map.service:BaseViewController
     * @description
     * Adds an accessory to the view.
     */
    addAccessory(accessory_object, name) {
        if (!name) {
            throw new Error('Must give a name for the accessory');
        }
        this.accessories[name] = accessory_object;
    }

    /**
     * @ngdoc method
     * @name startAccessories
     * @methodOf ims.map.service:BaseViewController
     * @description
     * Starts all of the view's accessories.
     */
    startAccessories() {
        _.forEach(this.accessories, function (accessory) {
            accessory.initialize();
        });
    }

    /**
     * @ngdoc method
     * @name stopAccessories
     * @methodOf ims.map.service:BaseViewController
     * @description
     * Stops all of the view's accessories.
     */
    stopAccessories() {
        _.forEach(this.accessories, function (accessory) {
            accessory.destroy();
        });
    }

    /**
     * @ngdoc method
     * @name setUpView
     * @methodOf ims.map.service:BaseViewController
     * @description
     * Used to do all the setup for a view such as setting map options and adding visualisers
     */
    setUpView(): { [key: string]: IMSVisualiser } {
        this.setAssetMapOptions(ASSET_DISPLAY_AGGREGATOR, {
            groupAs: MAP_OPTION_GROUP_DISPLAY,
            label: 'Aggregate data by',
            type: mapOptionTypes.dropdown,
            fullWidth: true,
            order: 2,
            value: {
                options: [createMapOptionValueOption('None')],
                defaultOption: 'None',
            },
            dependentValues: {},
        });

        // Setup for ims visualizers
        this.setAssetMapOptions(ASSET_DISPLAY_COLOUR, {
            groupAs: MAP_OPTION_GROUP_DISPLAY,
            label: 'Colour by',
            fullWidth: true,
            type: mapOptionTypes.dropdown,
            order: 1,
            value: {
                // This is set as an empty array as it is provided by ims visualisers
                options: [],
            },
            displayButton: true,
            displayButtonDescription: 'Show colour legend',
            displayButtonPanelTag: COLOR_LEGEND_ASSET_OPTIONS_TAG,
        });

        this.setAssetMapOptions(ASSET_LABEL_DISPLAY_VISIBILITY, {
            groupAs: VISIBILITY_GROUP,
            label: 'Asset labels',
            type: mapOptionTypes.dropdown,
            order: 1,
            value: {
                options: [SHOW_LABEL_NONE, SHOW_LABEL_IMMOBILE, SHOW_LABEL_ALWAYS].map(
                    displayStringToOptionValue,
                ),
                defaultOption: SHOW_LABEL_IMMOBILE,
            },
        });

        // Asset Label Type Option
        this.setAssetMapOptions(ASSET_LABEL_DISPLAY_TYPE, {
            groupAs: MAP_OPTION_GROUP_DISPLAY,
            label: 'Label asset by',
            type: mapOptionTypes.dropdown,
            fullWidth: true,
            order: 3,
            value: {
                options: [
                    createMapOptionValueOption(LABEL_TYPE_OPTION_NAME, 'Name'),
                    createMapOptionValueOption(
                        LABEL_TYPE_OPTION_COLOUR_BY_VALUE,
                        'Colour By Value',
                    ),
                ],
                defaultOption: 'name',
            },
            dependentValues: {},
        });

        this.setZoneMapOptions(ZONE_DISPLAY_AGGREGATOR, {
            groupAs: MAP_OPTION_GROUP_DISPLAY,
            label: 'Aggregate data by',
            type: mapOptionTypes.dropdown,
            fullWidth: false,
            order: 2,
            value: {
                options: [createMapOptionValueOption('None')],
                defaultOption: 'None',
            },
            dependentValues: {},
        });

        // Setup for ims visualizers
        this.setZoneMapOptions(ZONE_DISPLAY_COLOUR, {
            groupAs: MAP_OPTION_GROUP_DISPLAY,
            label: 'Colour by',
            fullWidth: false,
            type: mapOptionTypes.dropdown,
            order: 1,
            value: {
                // This is set as an empty array as it is provided by ims visualisers
                options: [],
            },
        });

        this.setZoneMapOptions(ZONE_LABEL_DISPLAY_VISIBILITY, {
            groupAs: VISIBILITY_GROUP,
            label: 'Labels',
            type: mapOptionTypes.dropdown,
            order: 1,
            value: {
                options: [SHOW_LABEL_NONE, SHOW_LABEL_ALWAYS].map(displayStringToOptionValue),
                defaultOption: SHOW_LABEL_ALWAYS,
            },
        });

        // Zone Label Type Option
        this.setZoneMapOptions(ZONE_LABEL_DISPLAY_TYPE, {
            groupAs: MAP_OPTION_GROUP_DISPLAY,
            label: 'Label by',
            type: mapOptionTypes.dropdown,
            fullWidth: false,
            order: 2,
            value: {
                options: [
                    createMapOptionValueOption(LABEL_TYPE_OPTION_NAME, 'Name'),
                    createMapOptionValueOption(
                        LABEL_TYPE_OPTION_COLOUR_BY_VALUE,
                        'Colour By Value',
                    ),
                ],
                defaultOption: 'name',
            },
            dependentValues: {},
        });

        this.addZoneImsVisualiser(this._zoneBaseColorVisualiser);
        this.addZoneImsVisualiser(this._zoneTypeVisualiser);
        this.addAssetImsVisualiser(this._assetBaseColorVisualiser);
        this.addAssetImsVisualiser(this._assetTypeVisualiser);
        this.applyViewComponentsAsMapOptions();
        return this.assetImsVisualisers;
    }

    /**
     * @ngdoc method
     * @name startView
     * @methodOf ims.map.service:BaseViewController
     * @param {View} imsView The imsView object from the api
     * @description
     * Ensures everything required to have a view active and running is fired.
     * NOTE: Please avoid overriding this for custom view start logic, and instead override setUpView
     */
    startView(imsView: View) {
        this.clearControllerValues();
        this._imsView = imsView;

        const assetImsVisualisers = this.setUpView();

        this.startAccessories();

        this._assetOptionsService.setNewMapOptions(
            this.availableAssetMapOptions,
            this.defaultAssetMapOptions,
            this._imsView,
            assetImsVisualisers,
        );

        this._zoneOptionsService.setNewMapOptions(
            this.availableZoneMapOptions,
            this.defaultZoneMapOptions,
            this._imsView,
        );
    }

    getView(): View {
        return this._imsView;
    }

    /**
     * @ngdoc method
     * @name stopView
     * @methodOf ims.map.service:BaseViewController
     * @description
     * Ensures everything required to shutdown a view and stop it running.
     */
    stopView() {
        this.stopAccessories();
    }

    newValueGetter(collection: any, key: string, operators: Operator[]): IMSValueGetter {
        return new IMSValueGetter(collection, key);
    }

    setAssetMapOptions(key: string, opt: MapOption) {
        opt.onChange = (option) => {
            this._assetOptionsService.setSelectedMapOption(key, option);
        };
        this.availableAssetMapOptions[key] = opt;

        const firstOption: string = opt.value.options
            ? extractValueFromOptionValue(opt.value.options[0])
            : undefined;

        const isCheckbox = opt.type == mapOptionTypes.checkbox;
        this.defaultAssetMapOptions[key] = isCheckbox
            ? true
            : opt.value.defaultOption || firstOption;
    }

    setZoneMapOptions(key: string, opt: MapOption) {
        const isCheckbox = opt.type == mapOptionTypes.checkbox;
        opt.onChange = (option) => {
            // mm yes, this is very good, I do enjoy this ternary
            const value = isCheckbox ? option.target.checked : option;
            this._zoneOptionsService.setSelectedMapOption(key, value);
        };
        this.availableZoneMapOptions[key] = opt;

        const firstOption: string = opt.value.options
            ? extractValueFromOptionValue(opt.value.options[0])
            : undefined;
        // oh boi the ternaries are real
        this.defaultZoneMapOptions[key] = isCheckbox
            ? true
            : opt.value.defaultOption || firstOption;
    }

    /**
     * Adds a new asset map option for this view.
     * @param {string} key
     * @param value value of the option
     * @param displayValue If present, will show this in the widget label instead of value
     * @param displayGroup If present, will use this for grouping of option values
     */
    addAssetMapOption(key: string, value: string, displayValue?: string, displayGroup?: string) {
        this.availableAssetMapOptions[key].value.options.push(
            createMapOptionValueOption(value, displayValue, displayGroup),
        );
    }

    /**
     * Adds a new zone map option for this view.
     * @param {string} key
     * @param value value of the option
     * @param displayValue If present, will show this in the widget label instead of value
     * @param displayGroup If present, will use this for grouping of option values
     */
    addZoneMapOption(key: string, value: string, displayValue?: string, displayGroup?: string) {
        this.availableZoneMapOptions[key].value.options.push(
            createMapOptionValueOption(value, displayValue, displayGroup),
        );
    }

    getStatDisplayFilters(): Array<StatDisplayFilterSchema> {
        return this._imsView.statDisplayFilters;
    }

    /**
     * Each time before the view starts, clear out controller state
     */
    protected clearControllerValues(): void {
        this.activeCollections = ['LocationStatCollection', 'TriggerStatCollection'];
        this.availableAssetMapOptions = {};
        this.defaultAssetMapOptions = { selectedImageryLayers: [] };
        this._assetImsVisualisers = {};
        this._zoneImsVisualisers = {};
        this._heatMapImsVisualisers = {};
    }
}
